import React, { useEffect, useState } from "react";
import { MapPin, ShieldCheck, Clock, Info } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { colors } from "@/lib/theme";
import { Toggle, Spinner } from "@/components/peeps/ui";

const RADII = [
  { m: 100, label: "100 m" },
  { m: 300, label: "300 m" },
  { m: 500, label: "500 m" },
  { m: 1000, label: "1 km" },
  { m: 5000, label: "5 km" },
];

export function ProximitySettings() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getProximityPrefs().then(setPrefs).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    try {
      const saved = await api.setProximityPrefs({
        scope: next.scope,
        radius_threshold: next.radius_threshold,
        default_enabled: next.default_enabled,
      });
      setPrefs(saved);
    } catch (e) {
      toast(e.message || "Couldn't save");
    }
  };

  if (loading || !prefs) {
    return <div className="flex justify-center py-6"><Spinner /></div>;
  }

  const on = prefs.default_enabled && prefs.scope === "global";

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
      <div className="flex items-center gap-3 p-4">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: colors.brandTertiary }}>
          <MapPin size={17} color={colors.brandSecondary} />
        </div>
        <div className="flex-1">
          <p className="font-display font-bold text-[15px]" style={{ color: colors.onSurface }}>Share location at events</p>
          <p className="font-display text-[12.5px]" style={{ color: colors.onSurfaceTertiary }}>On by default across all your groups</p>
        </div>
        <Toggle testID="proximity-global-toggle" on={on} onChange={(v) => save({ scope: v ? "global" : "off", default_enabled: v })} />
      </div>

      <div style={{ height: 1, background: colors.border, marginLeft: 16 }} />

      <div className="p-4" style={{ opacity: on ? 1 : 0.45, pointerEvents: on ? "auto" : "none" }}>
        <p className="font-display font-medium text-[11.5px] uppercase tracking-wide mb-2" style={{ color: colors.muted }}>Proximity radius</p>
        <div className="flex gap-2 flex-wrap">
          {RADII.map((r) => {
            const active = prefs.radius_threshold === r.m;
            return (
              <button key={r.m} data-testid={`radius-${r.m}`} onClick={() => save({ radius_threshold: r.m })}
                className="rounded-full py-1.5 px-3.5 font-display font-medium text-[12.5px]"
                style={{ background: active ? colors.brand + "26" : colors.surfaceTertiary, border: `1px solid ${active ? colors.brand : colors.border}`, color: active ? colors.brand : colors.onSurfaceTertiary }}>
                {r.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-4 pb-4 flex flex-col gap-2.5">
        <Explainer icon={Info} text="When an event is coming up, people in that group can see roughly how far away you are — never your exact address, and only within the radius you set." />
        <Explainer icon={Clock} text="It only turns on around the event (from an hour before to a few hours after), then switches off automatically. No location history is ever kept." />
        <Explainer icon={ShieldCheck} text="You can flip it off for any single event, and blocked people never see you. You only see others who are sharing too." />
        <p className="font-display text-[12px] mt-1" style={{ color: colors.faint }}>
          Per-group control lives in each group's settings. On an event, use the single toggle to override just that plan.
        </p>
      </div>
    </div>
  );
}

function Explainer({ icon: Icon, text }) {
  return (
    <div className="flex gap-2.5 items-start">
      <Icon size={15} color={colors.brandSecondary} style={{ marginTop: 2, flexShrink: 0 }} />
      <p className="font-display text-[12.5px] leading-snug" style={{ color: colors.onSurfaceTertiary }}>{text}</p>
    </div>
  );
}
